import { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { getInstallations } from 'src/bff';
import { useFaContent, useGetKvps } from 'src/feature-app';
import { arrayToObject, dealerTypeNumberToCode, logMessage, sanitizeKvps } from 'src/helpers';
import { DealersData, DealerType, Installation, Points } from 'src/types';
import { provinces } from '../NewMap';
import { useIsComerciales } from './use-is-comerciales';

export const installationsIDBuzz = ['0300W', '03910', '03394', '0311Q', '0311X', '52VH1', '0321C', '30559', '03200', '53V50', '0310W', '03917', '30083'];

export function useGetInstalaciones(token?: string, domain?: string, dealerType: DealerType = 'vn') {
	const dispatch = useDispatch();
	const [installations, setInstallations] = useState(null);
	const [dealerName, setDealerName] = useState(null);
	const [selectedDealer, setSelectedDealer] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const isComerciales = useIsComerciales();
	const store = useStore();
	const faContent = useFaContent();
	const { kvps: selectedKvps, isMyCurrentDealer } = useGetKvps();

	if (!token) token = isComerciales ? 'LCV_DDB_FRM_LEM_REC_LEM' : 'VW_DDB_FRM_LEM_REC_LEM';
	if (!domain) domain = 'VOLKSWAGEN_SF_LEAD_INSTALLATION_VN_VO_SERVICE';

	const getProvince = (provinceCode: string): string | null => {
		const province = provinces.find((p) => p.attributes.some((attr) => attr.value === provinceCode));
		return province ? province.attributes.find((attr) => attr.name === 'NAME')?.value?.toUpperCase() ?? null : null;
	};

	const filterInstallations = (installations: Installation[]) => {
		// Eliminamos instalaciones sin localizacion
		installations = installations.filter((instal) => instal.ShippingLatitude && instal.ShippingLongitude);

		// Filtramos por DealerType
		installations = installations.filter((instal) => dealerType == dealerTypeNumberToCode(instal.RCY_DealerType__c));

		// Filtro por provincias
		if (faContent?.provinces && faContent?.provinces?.length > 0) {
			// Obtenemos el prefijo de los códigos postales de provinces.ts seleccionadas en faContent.provinces. Añadimos un 0 si el código es menor que 10.
			const allowedPrefixes = provinces
				.filter((item) => faContent.provinces.includes(item.attributes.find((attr) => attr.name === 'NAME').value))
				.map((item) => {
					const code = item.attributes.find((attr) => attr.name === 'CODE').value;
					return parseInt(code, 10) < 10 ? `0${code}` : `${code}`;
				});

			// Filtramos las instalaciones por el prefijo. Si el código postal de la instalación empieza por alguno de los prefijos permitidos, se incluye.
			installations = installations.filter((instal) => {
				return instal.ShippingPostalCode && allowedPrefixes.some((prefix) => instal.ShippingPostalCode.startsWith(prefix));
			});
		}

		//installations = useFilterExcludedDealersInstallations(installations, store.getState().dealersInfo?.selectedCarline?.code);

		// Añadimos Province
		installations.forEach((instal, i) => {
			const provinceName = getProvince(instal.ShippingStateCode);
			installations[i].Province = provinceName ? provinceName : '';
		});

		return installations;
	};

	const createPoints = (installations: Installation[]) => {
		// Crear points. Necesario para el correcto funcionamiento del mapa.
		const points: Points[] = installations.map((instal) => {
			const dealer: Partial<DealersData> = {
				kvps: instal.KVPSCode__c,
				name: instal.VGED_CommercialName__c,
				markerInfo: {
					dealerInfo: {
						name: instal.VGED_CommercialName__c,
						address: instal.ShippingStreet,
						zipCode: instal.ShippingPostalCode,
						city: instal.ShippingCity,
						province: instal.Province,
					},
				},
			};

			const point: Points = {
				type: 'Feature',
				properties: {
					cluster: false,
					installation: instal,
					dealer,
				},
				geometry: {
					type: 'Point',
					coordinates: [Number(instal.ShippingLongitude), Number(instal.ShippingLatitude)] as [number, number],
				},
			};

			return point;
		});

		return points;
	};

	const requestInstallations = async () => {
		const installationsResponse = await getInstallations(token, domain);

		if (!Array.isArray(installationsResponse)) {
			setLoading(false);
			setError(true);
			console.error('Empty installations');
			return;
		}

		dispatch({ type: 'DEALERS_OR_INSTALLATIONS_MODE', payload: 'installations' });

		// Convertimos en un array manejable
		let installations = installationsResponse.map((instal) => arrayToObject(instal.attributes)) as Installation[];
		logMessage(installations, 'ALL INSTALLATIONS');

		// Antes de filtrar, buscamos installation si se require
		if (selectedKvps) {
			// Buscamos instalacion con el mismo Type
			let installation = installations.find((instal: Installation) => {
				return sanitizeKvps(instal.KVPSCode__c) == sanitizeKvps(selectedKvps) && dealerType == dealerTypeNumberToCode(instal.RCY_DealerType__c);
			}) as Installation;
			logMessage(installation, 'INSTALLATION');

			if (isMyCurrentDealer && installation) {
				dispatch({ type: 'SET_MY_CURRENT_INSTALLATION', payload: installation });
			}

			// Rebuscamos con cualquier Type
			if (!installation) {
				installation = installations.find((instal: Installation) => {
					return sanitizeKvps(instal.KVPSCode__c) == sanitizeKvps(selectedKvps);
				}) as Installation;
				logMessage(installation, 'TYPE INSTALLATION');
			}

			if (installation) {
				setSelectedDealer(installation);
				setDealerName(installation.VGED_CommercialName__c);

				// Si venimos del service MyDealer.currentDealer, guardamos la lista de concesiones (si existe) como installations.
				if (isMyCurrentDealer) {
					let concessions = installations.filter((instal: Installation) => {
						return instal.VGED_ConcessionDealerCode__c == installation.VGED_ConcessionDealerCode__c;
					}) as Installation[];

					if (concessions.length > 0) {
						concessions = filterInstallations(concessions);
						logMessage(concessions, 'CONCESSIONS');

						if (concessions.length > 0) {
							dispatch({ type: 'SET_MY_CURRENT_INSTALLATION', payload: installation });
						} else {
							dispatch({ type: 'SET_MY_CURRENT_INSTALLATION', payload: null });
							// Si la lista de concesiones queda vacia, guardamos instalaciones segun Type
							concessions = filterInstallations(installations);
							logMessage(concessions, 'TYPE CONCESSIONS');

							if (concessions.length == 0) {
								// Si continua vacia, devolvemos error
								setLoading(false);
								setError(true);
								console.error('Installations and concessions not found');
								return;
							}
						}
					} else {
						dispatch({ type: 'SET_MY_CURRENT_INSTALLATION', payload: null });
					}

					const points: Points[] = createPoints(concessions);

					setInstallations(concessions);
					dispatch({ type: 'SET_INSTALLATIONS', payload: concessions });
					dispatch({ type: 'SET_POINTS', payload: points });
					setLoading(false);
					return;
				} else {
					dispatch({ type: 'SET_SELECTED_INSTALLATION', payload: installation });
				}
				//dispatch({ type: 'UPDATE_SUGGESTION', payload: installation });
			} else {
				setSelectedDealer(null);
				setDealerName(null);
				console.warn('Installation not found');
			}
		}

		installations = filterInstallations(installations);
		logMessage(installations, 'INSTALLATIONS');

		if (installations.length == 0) {
			setLoading(false);
			setError(true);
			console.error('Installations not found');
			return;
		}

		const points: Points[] = createPoints(installations);

		setInstallations(installations);
		dispatch({ type: 'SET_INSTALLATIONS', payload: installations });
		dispatch({ type: 'SET_POINTS', payload: points });
		setLoading(false);
	};

	useEffect(() => {
		requestInstallations();
	}, []);

	return { installations, dealerName, selectedDealer, loading, error };
}
